import { CheckCircle } from '@phosphor-icons/react';
import bustlyLogo from '@common/assets/icons/logo.svg';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { authService } from '@common/api/authService';
import { supabase } from '@common/api/authService/supabase';

// 品牌图标 URL 映射表
const BRAND_ICONS: Record<string, string> = {
    'aliexpress': 'https://cdn.brandfetch.io/aliexpress.com/icon',
    'shopify': 'https://cdn.brandfetch.io/shopify.com/icon',
    'woocommerce': 'https://cdn.brandfetch.io/woocommerce.com/icon',
    'bigcommerce': 'https://cdn.brandfetch.io/bigcommerce.com/icon',
    'google-ads': 'https://cdn.brandfetch.io/ads.google.com/icon',
    'slack': 'https://cdn.brandfetch.io/slack.com/icon',
};

const getBrandIcon = (type: string) => {
    return BRAND_ICONS[type] || 'https://cdn.brandfetch.io/google.com/icon';
};

const OnboardingSuccessPage = () => {
    const [searchParams] = useSearchParams();
    const type = searchParams.get('type') || 'aliexpress';
    const [isConnected, setIsConnected] = useState(false);
    const [isChecking, setIsChecking] = useState(false);

    useEffect(() => {
        // 监听窗口关闭事件
        const handleWindowClose = () => {
            // 等待1秒后检查连接状态
            setIsChecking(true);
            setTimeout(async () => {
                try {
                    // 获取 workspace_id
                    const workspaceId = (window as any).Bustly?.workspace_id;
                    if (!workspaceId) {
                        console.error('Workspace ID not found');
                        setIsChecking(false);
                        return;
                    }

                    // 检查连接状态
                    const response = await authService.getWorkspaceIntegrations({
                        headers: { 'X-Workspace-Id': workspaceId }
                    });

                    const integrations = response.data || response;
                    const integration = integrations[type];

                    if (integration && integration[0]?.connected) {
                        setIsConnected(true);
                    }
                } catch (error) {
                    console.error('Error checking connection status:', error);
                } finally {
                    setIsChecking(false);
                }
            }, 1000);
        };

        // 监听窗口关闭（通过 beforeunload 和 pagehide）
        window.addEventListener('beforeunload', handleWindowClose);
        window.addEventListener('pagehide', handleWindowClose);

        return () => {
            window.removeEventListener('beforeunload', handleWindowClose);
            window.removeEventListener('pagehide', handleWindowClose);
        };
    }, [type]);

    return (
        <div className="w-full h-screen bg-white flex items-center justify-center">
            <div className="flex flex-col items-center justify-center gap-8">
                {/* Logos with connection line */}
                <div className="flex items-center gap-12 relative">
                    {/* BUSTLY Logo */}
                    <div className="relative z-10">
                        <div className="w-24 h-24 rounded-2xl bg-white shadow-lg border border-gray-100 flex items-center justify-center">
                            <img src={bustlyLogo} alt="BUSTLY" className="w-16 h-16" />
                        </div>
                    </div>

                    {/* Connection Line */}
                    <div className="absolute left-24 right-24 top-1/2 -translate-y-1/2 h-1 bg-gradient-to-r from-gray-200 to-gray-200 rounded-full" />

                    {/* Checkmark Badge */}
                    {isConnected && (
                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                            <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center shadow-lg animate-bounce">
                                <CheckCircle size={24} weight="fill" className="text-white" />
                            </div>
                        </div>
                    )}

                    {/* Integration Logo */}
                    <div className="relative z-10">
                        <div className="w-24 h-24 rounded-2xl bg-white shadow-lg border border-gray-100 flex items-center justify-center">
                            <img
                                src={getBrandIcon(type)}
                                alt={type}
                                className="w-16 h-16 object-contain"
                            />
                        </div>
                    </div>
                </div>

                {/* Success Message */}
                <div className="text-center space-y-2">
                    <h1 className="text-3xl font-bold text-gray-900">
                        Connection complete
                    </h1>
                    <p className="text-base text-gray-500">
                        You can close this window and continue in Bustly.
                    </p>
                </div>

                {/* Checking Status */}
                {isChecking && (
                    <div className="text-sm text-gray-400 flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                        Checking connection status...
                    </div>
                )}

                {/* Success Message when connected */}
                {isConnected && !isChecking && (
                    <div className="text-sm text-green-600 font-medium flex items-center gap-2">
                        <CheckCircle size={16} weight="fill" />
                        Successfully connected!
                    </div>
                )}
            </div>
        </div>
    );
};

export default OnboardingSuccessPage;
